import { useState, useRef, useEffect } from "react";
import { Outlet } from "react-router-dom";
import SideBar from "../sidebar/SideBar";
import Header from "../header/Header";
import { requestSwitchClinic } from "../../../api/auth/user";
import { showErrorToast } from "../../../utils/toaster";

const BasicLayout = ({ children, protectedRouteRef }) => {
  const [collapse, setCollapse] = useState(true);
  const [mobileSidebarOpen, setMobileSideBarOpen] = useState(false);
  const sidebarRef = useRef(null);
  const overlayRef = useRef(null);

  /* Collapse function */
  const handleCollapse = () => {
    setCollapse(!collapse);
  };

  const handleMobSideBar = () => {
    setMobileSideBarOpen(!mobileSidebarOpen);
  };

  // Handle clicks outside the sidebar to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        mobileSidebarOpen &&
        overlayRef.current &&
        overlayRef.current === event.target
      ) {
        setMobileSideBarOpen(false);
      }
    };

    // Add event listener when sidebar is open
    if (mobileSidebarOpen) {
      document.addEventListener("click", handleClickOutside);
    }

    // Cleanup event listener
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [mobileSidebarOpen]);

  // Handle escape key to close sidebar
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === "Escape" && mobileSidebarOpen) {
        setMobileSideBarOpen(false);
      }
    };

    if (mobileSidebarOpen) {
      document.addEventListener("keydown", handleEscapeKey);
    }

    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [mobileSidebarOpen]);

  const [loadingSwitchCLinic, setLoadingSwitchClinic] = useState(false);

  const handleSwitchClinic = async (id) => {
    try {
      setLoadingSwitchClinic(true);
      const payload = { clinic_id: id };

      const response = await requestSwitchClinic(payload);
      const token = response.data.data.token;
      localStorage.setItem("token", token);

      protectedRouteRef.current?.refreshAuth();
    } catch (error) {
      showErrorToast(error);
    } finally {
      setLoadingSwitchClinic(false);
    }
  };

  return (
    <div className="">
      {/* <!-- ===== Page Wrapper Start ===== --> */}
      <div className="flex h-screen overflow-x-hidden scrollbar-hidden">
        {/* <!-- ===== Sidebar Start ===== --> */}

        {/* Mobile View with smooth transition */}
        <div
          ref={overlayRef}
          className={`fixed z-50 overflow-y-auto topindex scrollbar-hidden h-full duration-300 ease-in-out bg-darkgrey bg-opacity-30 w-full md:hidden transition-transform
          ${mobileSidebarOpen ? "translate-x-0" : "-translate-x-full"}
          `}
        >
          <div
            ref={sidebarRef}
            className=" h-screen w-auto max-w-[1px]  transition-transform duration-300 ease-in-out"
          >
            <SideBar handleSwitchClinic={handleSwitchClinic} collapse={false} />
          </div>
        </div>
        {/* Mobile View End */}

        {/* Desktop view */}
        <div className="hidden md:block">
          <SideBar
            collapse={collapse}
            handleSwitchClinic={handleSwitchClinic}
          />
        </div>
        <div className="relative hidden md:block">
          {/* <!-- ===== Collapse Button ===== --> */}
          <button
            onClick={() => handleCollapse()}
            aria-controls="sidebar"
            style={{ zIndex: 999 }}
            className="absolute md:-right-4 md:top-3 lg:bottom-20 w-[43px] h-[44px] rounded-full bg-[#219FD9] flex justify-center items-center"
          >
            <div className={`${collapse ? "rotate-180" : ""} duration-300`}>
              <img src="/icons/arrow.svg" className="" alt="" />
            </div>
          </button>
        </div>
        {/* Desktop view End*/}
        {/* <!-- ===== Sidebar End ===== --> */}

        {/* <!-- ===== Content Area Start ===== --> */}
        <div className="relative flex flex-1 flex-col overflow-y-auto overflow-x-hidden main-content">
          {/* <!-- ===== Header Start ===== --> */}
          <Header handleMobSideBar={handleMobSideBar} />
          {/* <!-- ===== Header End ===== --> */}

          {/* <!-- ===== Main Content Start ===== --> */}
          <main>
            <div className="mx-auto max-w-screen-2xl p-4 md:p-6 2xl:pt-6 h-[90vh] mb-10 lg:mb-0">
              <Outlet />
              {children}
            </div>
          </main>
          {/* <!-- ===== Main Content End ===== --> */}
        </div>
        {/* <!-- ===== Content Area End ===== --> */}
      </div>
      {/* <!-- ===== Page Wrapper End ===== --> */}
    </div>
  );
};

export default BasicLayout;
